import {
  type Element as EolibElement,
  type EoReader,
  PacketAction,
  type PacketFamily,
} from 'eolib';
import type { Client } from '@/client';
import {
  eolibToCombatElement,
  npcElementEffectiveness,
} from '@/combat-overlay/element-colors';
import type { SkillCatalogEntry } from '@/controllers';

const FAMILY_SKILL = 62;

function readStr(reader: EoReader): string {
  const len = reader.getChar();
  return new TextDecoder().decode(new Uint8Array(reader.getBytes(len)));
}

function handleSkillList(client: Client, reader: EoReader): void {
  const count = reader.getShort();
  const entries: SkillCatalogEntry[] = [];
  for (let i = 0; i < count; i++) {
    entries.push({
      skillId: readStr(reader),
      name: readStr(reader),
      iconId: readStr(reader),
      costHp: reader.getShort(),
      costTp: reader.getShort(),
      cooldownMs: reader.getInt(),
      target: reader.getChar() === 1 ? 'enemy' : 'self',
      range: reader.getChar(),
      castVfx: readStr(reader),
      impactVfx: readStr(reader),
      classRequirement: reader.getShort(),
      minLevel: reader.getChar(),
      skillPointCost: reader.getShort(),
      // Trailing field — must stay last, mirroring the server's serialize
      // order in deep/skill_list_server_packet.rs.
      description: readStr(reader),
    });
  }
  client.skillController.setCatalog(entries);
}

function handleSkillKnown(client: Client, reader: EoReader): void {
  const count = reader.getShort();
  const ids: string[] = [];
  for (let i = 0; i < count; i++) ids.push(readStr(reader));
  const points = reader.getInt();
  client.skillController.setKnown(ids, points);
}

function handleSkillLearnReply(client: Client, reader: EoReader): void {
  // Wire order deliberately differs from the server struct's field order:
  // skill_id, ok, reason, skill_points_remaining.
  const skillId = readStr(reader);
  const ok = reader.getChar() !== 0;
  const reason = reader.getChar();
  const remaining = reader.getInt();
  client.skillController.applyLearnReply(ok, reason, skillId, remaining);
}

function handleSkillReply(client: Client, reader: EoReader): void {
  const skillId = readStr(reader);
  const ok = reader.getChar() !== 0;
  const reason = reader.getChar();
  const cooldownMs = reader.getInt();
  client.skillController.handleReply(skillId, ok, reason, cooldownMs);

  if (ok) {
    const entry = client.skillController.entryOf(skillId);
    const casterKey = `char:${client.playerId}`;
    const pos = client.mapRenderer?.getEntityWorldSpritePosition(casterKey);
    if (entry?.castVfx && pos) {
      client.skillVfxController.playBurst(
        entry.castVfx,
        pos.x,
        pos.y,
        casterKey,
      );
    }
  } else {
    client.skillController.clearPending(skillId);
  }
}

function handleSkillDamage(client: Client, reader: EoReader): void {
  const casterId = reader.getShort();
  const npcIndex = reader.getShort();
  const damage = reader.getInt();
  const hpPercentage = reader.getChar();
  const element = reader.getChar();

  const npc = client.getNpcByIndex(npcIndex);
  if (!npc) {
    client.sessionController.requestNpcRange([npcIndex]);
    return;
  }
  client.combatOverlayController.setHpBar({
    key: `npc:${npcIndex}`,
    percent: hpPercentage,
    worldX: npc.coords.x,
    worldY: npc.coords.y,
  });
  if (damage > 0) {
    const record = client.getEnfRecordById(npc.id);
    const combatElement = eolibToCombatElement(element as EolibElement);
    client.combatOverlayController.pushDamage({
      amount: damage,
      element: combatElement,
      effectiveness: record
        ? npcElementEffectiveness(
            combatElement,
            record.element,
            record.elementWeakness,
          )
        : 'neutral',
      targetKey: `npc:${npcIndex}`,
      worldX: npc.coords.x,
      worldY: npc.coords.y,
    });
  } else {
    client.combatOverlayController.pushMiss({
      targetKey: `npc:${npcIndex}`,
      worldX: npc.coords.x,
      worldY: npc.coords.y,
    });
  }

  if (casterId === client.playerId) {
    const entry = client.skillController.takePendingImpact(npcIndex);
    const targetKey = `npc:${npcIndex}`;
    const pos = client.mapRenderer?.getEntityWorldSpritePosition(targetKey);
    if (entry?.impactVfx && pos) {
      client.skillVfxController.playBurst(
        entry.impactVfx,
        pos.x,
        pos.y,
        targetKey,
      );
    }
  }
}

function handleSkillAugment(client: Client, reader: EoReader): void {
  const npcIndex = reader.getShort();
  client.combatOverlayController.markAugmented(`npc:${npcIndex}`);
}

export function registerSkillHandlers(client: Client): void {
  client.bus!.registerPacketHandler(
    FAMILY_SKILL as PacketFamily,
    PacketAction.List,
    (reader) => handleSkillList(client, reader),
  );
  client.bus!.registerPacketHandler(
    FAMILY_SKILL as PacketFamily,
    PacketAction.Reply,
    (reader) => handleSkillReply(client, reader),
  );
  client.bus!.registerPacketHandler(
    FAMILY_SKILL as PacketFamily,
    PacketAction.Spec,
    (reader) => handleSkillDamage(client, reader),
  );
  client.bus!.registerPacketHandler(
    FAMILY_SKILL as PacketFamily,
    PacketAction.Agree,
    (reader) => handleSkillAugment(client, reader),
  );
  client.bus!.registerPacketHandler(
    FAMILY_SKILL as PacketFamily,
    PacketAction.Player,
    (reader) => handleSkillKnown(client, reader),
  );
  client.bus!.registerPacketHandler(
    FAMILY_SKILL as PacketFamily,
    PacketAction.Accept,
    (reader) => handleSkillLearnReply(client, reader),
  );
}
